/**
 * A stored spine-token record -> the custom-property block a branded surface
 * publishes. THE generic emitter: every brand goes through this one function,
 * which is what makes a brand a DB record instead of a `.ts` file named after
 * it.
 *
 * ## One set of values, every vocabulary
 *
 * The spine's rules read the contract names (`--bg`, `--surface`, `--accent`);
 * the anchor CSS ported into the spine reads the hub dialect (`--p2`, `--o`,
 * `--tx`). They are aliases of one set of values — the bridge identities
 * `tokens.scss:131-133` declares (`--p1 ≡ --bg-3`, `--p2 ≡ --surface`,
 * `--p3 ≡ --surface-2`) — so the record stores each value ONCE and this
 * emitter publishes it under every name that reads it. A brand cannot desync
 * the vocabularies, because it never writes them separately.
 *
 * ## Absent means the platform default, not a guess
 *
 * Only fields present in the record are emitted. An unset field falls through
 * to the value `tokens.scss` already declares — a brand that only sets
 * `accent` is complete. Nothing here interpolates or invents a hex.
 *
 * ## Emitted as an element style, not a stylesheet
 *
 * Same reasoning `brandStyle` records: a brand's colours in a global sheet
 * are a global change; on the element they scope themselves.
 */

import React from "react";
import type {SurfaceSpineTokens} from "@devour/client";

/*
 * Key guard for the two open maps (`earnCategories`, `extraVars`). The
 * backend validates on write, but the record crosses a network and a
 * database; a malformed key is dropped here rather than handed to CSSOM.
 */
const CSS_KEY_RE = /^[a-zA-Z0-9-]{1,60}$/;

/**
 * Build the custom-property block for a surface from its stored record.
 * Returns undefined when the record is absent or empty so callers can spread
 * `?? {}` or fall through to another source.
 */
export function spineTokenStyle(tokens: SurfaceSpineTokens | undefined | null): React.CSSProperties | undefined {
    if (!tokens) return undefined;

    const style: Record<string, string> = {};
    const put = (names: readonly string[], value?: string): void => {
        if (!value) return;
        for (const name of names) style[name] = value;
    };

    put(["--bg"], tokens.bg);
    put(["--bg-2"], tokens.bg2);
    put(["--bg-3", "--p1"], tokens.bg3);
    put(["--surface", "--p2"], tokens.surface);
    put(["--surface-2", "--p3"], tokens.surface2);
    put(["--line", "--ln"], tokens.line);
    put(["--line-2", "--ln2"], tokens.line2);
    put(["--line-3"], tokens.line3);
    put(["--text", "--tx"], tokens.text);
    put(["--text-2", "--t2"], tokens.text2);
    put(["--text-3", "--t3"], tokens.text3);
    put(["--text-faint"], tokens.textFaint);
    put(["--accent", "--o"], tokens.accent);
    put(["--accent-2", "--o2"], tokens.accent2);
    put(["--accent-glow"], tokens.accentGlow);
    put(["--accent-ink"], tokens.accentInk);
    /*
     * `--ink` is a FILL despite the dialect name — the anchor reads it only
     * as the dark stop of gradients, never as type. The TYPE role is
     * `--accent-ink` above; conflating them is the recorded Minelab defect.
     */
    put(["--ink"], tokens.accentDeep);
    put(["--octa"], tokens.onAccent);
    put(["--oi"], tokens.chipInk);
    put(["--ok"], tokens.ok);
    /*
     * The --surface-* names are the ones the spine actually reads: it must
     * declare its own `--display`/`--body` defaults on `.surface-hub`, and an
     * element-level declaration beats these inherited wrapper vars — so the
     * spine's defaults are written as `var(--surface-display, <fallback>)`
     * and the brand value travels under the name nothing redeclares.
     */
    put(["--display", "--dp", "--surface-display"], tokens.displayFont);
    put(["--body", "--bd", "--surface-body"], tokens.bodyFont);
    put(["--r"], tokens.radius);
    put(["--r2"], tokens.radius2);
    put(["--bv"], tokens.bevel);
    put(["--colh"], tokens.columnHeight);

    for (const [key, value] of Object.entries(tokens.earnCategories ?? {})) {
        if (CSS_KEY_RE.test(key) && value) style[`--earn-category-${key}`] = value;
    }
    for (const [key, value] of Object.entries(tokens.extraVars ?? {})) {
        if (CSS_KEY_RE.test(key) && value) style[`--${key}`] = value;
    }

    return Object.keys(style).length > 0 ? style as React.CSSProperties : undefined;
}
